import {
  Component, EventEmitter, Input, Output, OnChanges,
  SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef
} from '@angular/core';
import { TutorialStep } from '../../models';
import { TutorialService } from '../../services/tutorial.service';

@Component({
  selector: 'app-step-form-dialog',
  templateUrl: './step-form-dialog.component.html',
  styleUrls: ['./step-form-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class StepFormDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() step: TutorialStep | null = null;
  @Input() nextStepNumber = 1;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<{ step: Record<string, any>; imageFile: File | null }>();

  stepNumber = 1;
  title = '';
  body = '';
  imageUrl = '';
  imagePreview = '';
  imageFile: File | null = null;
  /** Tracks whether a file is being dragged over the upload area */
  isDragOver = false;

  /** Backend stores Body as a 4000-char column; count the full HTML string */
  readonly maxBodyLength = 4000;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly tutorialService: TutorialService
  ) {}

  get isEditMode(): boolean {
    return this.step !== null;
  }

  get dialogTitle(): string {
    return this.isEditMode ? 'Edit Step' : 'Add Step';
  }

  /** Current length of the Body HTML that will be sent to the backend */
  get bodyLength(): number {
    return this.body?.length ?? 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.imageFile = null;
      if (this.step) {
        this.stepNumber = this.step.StepNumber;
        this.title = this.step.Title;
        this.body = this.step.Body;
        this.imageUrl = this.step.ImageUrl;
        this.imagePreview = this.step.ImageUrl
          ? this.tutorialService.getStepImageUrl(this.step)
          : '';
      } else {
        this.stepNumber = this.nextStepNumber;
        this.title = '';
        this.body = '';
        this.imageUrl = '';
        this.imagePreview = '';
      }
    }
  }

  /** Quill 1.3.7 converts loaded HTML into one code block if the cursor is in a code block; ignore cursor format on loads */
  onEditorInit({ editor }: { editor: any }): void {
    const clipboard = editor.clipboard;
    const originalConvert = clipboard.convert.bind(clipboard);
    clipboard.convert = (html?: string) => {
      if (typeof html !== 'string') { return originalConvert(html); }
      const originalGetFormat = editor.getFormat;
      editor.getFormat = () => ({});
      try {
        return originalConvert(html);
      } finally {
        editor.getFormat = originalGetFormat;
      }
    };
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) { return; }
    const file = input.files[0];
    if (!file.type.startsWith('image/')) { return; }
    this.imageFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result as string;
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  /** Prevents default browser behavior and highlights the drop zone */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  /** Removes the drop zone highlight when the dragged file leaves the area */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  /** Handles file dropped onto the upload area — validates image type and loads preview */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file || !file.type.startsWith('image/')) { return; }
    this.imageFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result as string;
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  removeImage(): void {
    this.imageFile = null;
    this.imageUrl = '';
    this.imagePreview = '';
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onSave(): void {
    if (!this.title.trim()) { return; }
    if (this.bodyLength > this.maxBodyLength) { return; }
    this.save.emit({
      step: {
        StepNumber: this.stepNumber,
        Title: this.title.trim(),
        Body: this.cleanBody(this.body),
        ImageUrl: this.imageUrl
      },
      imageFile: this.imageFile
    });
    this.onClose();
  }

  /** Strips Quill's empty-content placeholder so blank bodies persist as '' */
  private cleanBody(html: string): string {
    const value = (html ?? '').trim();
    if (!value || value === '<p><br></p>') { return ''; }
    return value;
  }

  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
